import { headerMediaQueryLarge } from "@theme/header-breakpoint";
import { calculateHeaderGroupHeight } from "@theme/critical";
import { Component } from "@theme/component";
import { onDocumentLoaded, changeMetaThemeColor } from "@theme/utilities";

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ["headerDrawerContainer", "headerMenu", "headerRowTop"];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * Scroll position when direction last changed (for threshold calculation)
   * @type {number}
   */
  #directionChangeScrollTop = 0;

  /**
   * Current scroll direction ('up' | 'down' | 'none')
   * @type {string}
   */
  #currentDirection = "none";

  /**
   * Minimum scroll distance (in px) before triggering hide/show
   * @type {number}
   */
  static SCROLL_THRESHOLD = 200;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume.
   * When dual headers exist (transparent + sticky overlay), only the visible one drives --header-height.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const isOverlay = this.hasAttribute("data-sticky-overlay");
    if (isOverlay && !this.dataset.visible) return;
    const overlay = document.getElementById("header-sticky-overlay");
    if (!isOverlay && overlay?.dataset.visible) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty("--header-height", `${height}px`);

    // Keep the drawer icon strictly mobile-only.
    if (headerMediaQueryLarge.matches) {
      this.#updateMenuVisibility(false);
      return;
    }

    // Check if the menu drawer should be hidden in favor of the header menu
    if (
      this.#menuDrawerHiddenWidth &&
      window.innerWidth > this.#menuDrawerHiddenWidth
    ) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? "inactive" : "active";
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen =
          !isIntersecting || this.dataset.stickyState === "active";
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove("header-desktop:hidden");
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add("hidden");
    } else {
      this.refs.headerDrawerContainer.classList.add("header-desktop:hidden");
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove("hidden");
    }
  }

  /**
   * Updates data-visible on the sticky overlay based on scroll (one viewport height threshold).
   * @returns {boolean} Whether the overlay is currently visible
   */
  #updateStickyOverlayVisibility() {
    if (!this.hasAttribute("data-sticky-overlay")) return false;
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const viewportHeight = window.innerHeight;
    const visible = scrollTop >= viewportHeight;
    if (visible) {
      this.dataset.visible = "true";
      const { height } = this.getBoundingClientRect();
      document.body.style.setProperty("--header-height", `${height}px`);
    } else {
      delete this.dataset.visible;
      const staticHeader = document.getElementById("header-component");
      if (staticHeader) {
        const { height } = staticHeader.getBoundingClientRect();
        document.body.style.setProperty("--header-height", `${height}px`);
      }
    }
    return visible;
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute("sticky");
    const isStickyOverlay = this.hasAttribute("data-sticky-overlay");
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    // Sticky overlay: show after one viewport height, hide when scrolled back up
    if (isStickyOverlay) {
      const overlayVisible = this.#updateStickyOverlayVisibility();
      if (!overlayVisible) {
        this.#lastScrollTop = scrollTop;
        return;
      }
      // When visible, continue to normal sticky scroll logic (hide on scroll down, show on scroll up)
    }

    if (!this.#offscreen && stickyMode !== "always") {
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    // Handle hide-on-scroll-down for always sticky headers (including transparent headers that became sticky)
    if (stickyMode === "always") {
      // For sticky overlay (position: fixed; top: 0), getBoundingClientRect().top is always 0,
      // so we check if it's the overlay - if visible, we're never "at top" in the meaningful sense.
      // For regular sticky headers, use the bounding rect check.
      const isAtTop = isStickyOverlay
        ? false
        : this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = "none";
        this.removeAttribute("data-header-hidden");
        this.#currentDirection = "none";
        this.#directionChangeScrollTop = scrollTop;
      } else {
        // Determine intended direction
        const intendedDirection = isScrollingUp ? "up" : "down";
        const scrollDelta = Math.abs(
          scrollTop - this.#directionChangeScrollTop,
        );

        // Only change direction if we've scrolled past the threshold
        if (intendedDirection !== this.#currentDirection) {
          if (scrollDelta >= HeaderComponent.SCROLL_THRESHOLD) {
            // Direction change confirmed - apply it
            this.#currentDirection = intendedDirection;
            this.#directionChangeScrollTop = scrollTop;

            if (intendedDirection === "up") {
              this.dataset.scrollDirection = "up";
              this.removeAttribute("data-header-hidden");
            } else {
              this.dataset.scrollDirection = "down";
              this.setAttribute("data-header-hidden", "");
            }
          }
          // If under threshold, don't change direction yet
        }
        // If same direction, just continue (no state change needed)
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute("data-animating");

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = "inactive";
        this.dataset.scrollDirection = "none";
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = "active";
        this.dataset.scrollDirection = "up";
      }
    } else if (this.dataset.stickyState === "active") {
      this.dataset.scrollDirection = "none";
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute("data-animating", "");

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = "idle";
        this.removeAttribute("data-animating");
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = "none";
      this.dataset.stickyState = "idle";
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener("overflowMinimum", this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute("sticky");
    const isStickyOverlay = this.hasAttribute("data-sticky-overlay");

    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === "always");

      if (stickyMode === "scroll-up" || stickyMode === "always") {
        document.addEventListener("scroll", this.#handleWindowScroll);
      }
    }

    if (isStickyOverlay) {
      this.#updateStickyOverlayVisibility();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener("overflowMinimum", this.#handleOverflowMinimum);
    document.removeEventListener("scroll", this.#handleWindowScroll);
    document.body.style.setProperty("--header-height", "0px");
  }
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector("#header-component");
  const headerGroup = document.querySelector("#header-group");

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() =>
      calculateHeaderGroupHeight(header, headerGroup),
    );

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement))
              continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
